import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { memo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import ImageCarousel from '../../../components/Image/ImageCarousel/ImageCarousel';
import ProgressiveImage from '../../../components/Image/ProgressiveImage';
import Modal from '../../../components/Modal/Modal';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { getSharetribeImageProps } from '../../../util/hooks/useSharetribeImage';
import {
  LISTING_PAGE_CAROUSEL_MODAL_SCROLL_LAYER,
  LISTING_PAGE_GALLERY_MODAL_SCROLL_LAYER,
  ListingPageGlobalStyles,
} from '../listingPageStyles';
import ListingGallery from './ListingGallery';
import useGalleryHistory from './useGalleryHistory';
import SwiperMobile from '../SwiperMobile';

// former ListingPage.module.css .carouselModalContainer
// Passed as Modal containerSx (no containerClassName), which REPLACES the container base styles —
// exactly as the original containerClassName={css.carouselModalContainer} did.
const carouselModalContainerSx = () => ({
  width: '100%',
  height: '100%',
});

// Gallery overlay container: plain full-size block; the white background and
// scrolling live on the scroll layer class (LISTING_PAGE_GALLERY_MODAL_SCROLL_LAYER).
const galleryModalContainerSx = () => ({
  width: '100%',
  minHeight: '100%',
});

// Floating back button for the gallery overlay (fixed so it survives scrolling).
const galleryBackButtonSx = {
  position: 'fixed',
  top: 16,
  left: 16,
  zIndex: 2,
  p: 1,
  color: 'black',
  backgroundColor: 'white',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
  '&:hover': {
    backgroundColor: 'white',
    color: 'black',
  },
};

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    border: `2px solid var(--marketplaceColorDark)`,
    backgroundColor: 'var(--marketplaceColorDark)',
    color: 'var(--marketplaceColorLight)',
    padding: '0 4px',
    top: '-5px',
    right: '-5px',
  },
}));
// General Base Button Style
const baseButtonStyle = {
  position: 'absolute',
  zIndex: 2,
  borderRadius: '200px',
  backgroundColor: 'white',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'white',
  },
};

// Back Button Style
const backButtonStyle = {
  ...baseButtonStyle,
  top: 16,
  left: 16,
  p: 2,
  color: 'black', // Specific to backButton
  '&:hover': {
    ...baseButtonStyle['&:hover'],
    color: 'black',
  },
};

// Photos Button Style
const desktopPhotosButton = {
  ...baseButtonStyle,
  bottom: '10px',
  right: '10px',
};
const mobilePhotosButton = {
  ...baseButtonStyle,
  top: '16px',
  right: '10px',
};

// Surround Button Style
const surroundButtonDesktopStyle = {
  ...baseButtonStyle,
  bottom: '10px',
  right: '140px',
};
const surroundButtonMobileStyle = {
  ...baseButtonStyle,
  top: '16px',
  right: '128px',
};

const aspectRatioPadding = '56%'; // Assuming a 16:9 aspect ratio for the placeholder

// Rendered widths of the desktop hero grid. The grid lives in a `maxWidth="xl"`
// Container (theme xl = 1440px) with 24px side padding, so the content box caps at
// 1392px; the 8/12 tile is ~930px wide, the 4/12 tiles ~460px. Below the md
// breakpoint every tile is full width (the Grid falls back to xs=12) — that branch
// is normally the Swiper's, but a desktop UA in a narrow window can hit it before
// the post-hydration resize flips useScreenSize.
const MAIN_TILE_SIZES = '(max-width: 899px) 100vw, (min-width: 1440px) 930px, 66vw';
const SIDE_TILE_SIZES = '(max-width: 899px) 100vw, (min-width: 1440px) 460px, 33vw';
const SINGLE_TILE_SIZES = '(min-width: 1440px) 1392px, 100vw';

// Used for the Image Layout Grid
const ImageBox = ({ image, title, onClick, sizes, isFirst = false }) => (
  <Box sx={{ height: '100%' }}>
    <ListingImage
      handleViewPhotosClick={onClick}
      image={image}
      isFirst={isFirst}
      sizes={sizes}
      title={title}
    />
  </Box>
);

const ListingImage = ({ title, image, handleViewPhotosClick, sizes, isFirst = false }) => {
  return (
    <Box
      onClick={(e) => handleViewPhotosClick('image', e)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&:before': {
          content: '""',
          display: 'block',
          paddingTop: aspectRatioPadding, // Creates the aspect ratio space
        },
        '&:hover': {
          cursor: 'pointer',
          filter: 'brightness(80%)',
          transition: '0.2s',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <ProgressiveImage
          addHelmet={isFirst}
          alt={title}
          blurSrc={image.blurSrc}
          blurSrcst={image.blurSrcSet}
          loadLazy={!isFirst}
          sizes={sizes}
          src={image.src}
          srcset={image.srcset}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center center',
          }}
        />
      </Box>
    </Box>
  );
};

// This mapping over listing.images processes each image and categorizes them based on their metadata.
// If imagesMetadata is not present, all images are considered default images.
// If imagesMetadata is an array, it searches for a matching entry by image id and determines if an image is a surround image.
// If imagesMetadata is an object, it directly checks the image id's property to determine if it is a surround image.
// Based on the determination, images are pushed to either defaultImages or surroundImages arrays.
// The surroundImages array is used for a separate group of images, possibly for a different view or feature in the UI.
const categorizeListingImages = (images, imagesMetadata) => {
  const defaultImages = [];
  const surroundImages = [];

  images.forEach((image) => {
    // No metadata, treat all as default images
    if (!imagesMetadata) {
      defaultImages.push(image);
      return;
    }

    // Metadata is an array, find the image metadata entry by image id
    if (Array.isArray(imagesMetadata)) {
      const imageMetadata = imagesMetadata.find((imageMeta) => imageMeta.id === image.id.uuid);
      // If found and marked as surround image, categorize accordingly
      if (imageMetadata?.surroundImage) {
        image.surroundImage = true;
        surroundImages.push(image);
      } else {
        defaultImages.push(image);
      }
      return;
    }

    // Metadata is an object, check the image id property for surround image flag
    if (typeof imagesMetadata === 'object') {
      if (imagesMetadata[image.id.uuid]?.surroundImage) {
        image.surroundImage = true;
        surroundImages.push(image);
      } else {
        defaultImages.push(image);
      }
    }
  });

  return { defaultImages, surroundImages };
};

const processListingImages = (images, count, isMobile, isMainImage = false) => {
  const baseVariants = ['landscape-blur', 'landscape-crop-x05', 'landscape-crop1x'];
  const extendedVariants = [...baseVariants, 'landscape-crop2x'];

  const selectedVariants = isMainImage ? extendedVariants : baseVariants;

  const processedImages = images.slice(0, count).map((image) => {
    return getSharetribeImageProps(image, selectedVariants);
  });

  processedImages.forEach((image, index) => {
    if (isMobile) {
      image.style = {
        height: '300px',
      };
    }
  });

  // Ensure the array has `count` elements, duplicating the first image if necessary
  while (processedImages.length < count) {
    processedImages.push(processedImages[0] || null);
  }

  return processedImages;
};

// A function to decide which layout to render based on image count
const renderImageGrid = (imageCount, images, title, handleViewPhotosClick) => {
  if (imageCount > 2) {
    return (
      <Grid
        columnSpacing={1}
        container
        sx={{
          height: '100%',
        }}
      >
        <Grid size={{ md: 8, xs: 12 }}>
          <ImageBox
            image={images[0]}
            isFirst
            onClick={handleViewPhotosClick}
            sizes={MAIN_TILE_SIZES}
            title={title}
          />
        </Grid>
        <Grid container rowSpacing={1} size={{ md: 4, xs: 12 }}>
          <Grid size={12}>
            <ImageBox
              image={images[1]}
              onClick={handleViewPhotosClick}
              sizes={SIDE_TILE_SIZES}
              title={title}
            />
          </Grid>
          <Grid size={12}>
            <ImageBox
              image={images[2]}
              onClick={handleViewPhotosClick}
              sizes={SIDE_TILE_SIZES}
              title={title}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  } else if (imageCount > 0) {
    return (
      <Grid container height="512px">
        <Grid height="100%" size={12}>
          <ImageBox
            image={images[0]}
            isFirst
            onClick={handleViewPhotosClick}
            sizes={SINGLE_TILE_SIZES}
            title={title}
          />
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <HelpOutlineOutlinedIcon sx={{ fontSize: '512px' }} />
        <Typography variant="h5">
          <FormattedMessage id="ListingPage.noImages" />
        </Typography>
      </Box>
    );
  }
};

const SectionImages = ({ title, listing, isOwnListing }) => {
  const dispatch = useDispatch();
  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));
  const navigate = useNavigate();

  /** RESPONSIVENESS **/
  const { isMobile, isTablet } = useScreenSize();

  /** STATES **/
  const [surroundImageCarouselOpen, setSurroundImageCarouselOpen] = useState(false);

  /** IMAGE DATA **/
  const hasImages = listing.images && listing.images.length > 0;
  const imagesMetadata = listing.attributes.publicData?.imagesMetadata;
  const { defaultImages, surroundImages } = categorizeListingImages(listing.images, imagesMetadata); // This mapping over listing.images processes each image and categorizes them based on their metadata.
  const processedImages = processListingImages(defaultImages, defaultImages.length, isMobile, true); // Pass isMainImage=true for the first few

  const intl = useIntl();
  const { galleryOpen, photoIndex, openGallery, openPhoto, goBack } = useGalleryHistory(
    defaultImages.length
  );
  // Spec: no gallery access with 0 images — a deep-linked '#gallery' on an
  // imageless (or surround-only) listing must not open an empty overlay.
  const showGallery = galleryOpen && defaultImages.length > 0;

  /** FUNCTIONS **/
  const handleViewPhotosClick = (type) => {
    if (type === 'image') {
      openGallery();
    } else {
      setSurroundImageCarouselOpen(true);
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  /** COMPONENTS **/
  const viewPhotosButton = hasImages ? (
    <Button
      onClick={(e) => handleViewPhotosClick('image', e)}
      size="small"
      startIcon={<InsertPhotoOutlinedIcon />}
      sx={!isTablet ? desktopPhotosButton : mobilePhotosButton}
      variant="contained"
    >
      <StyledBadge badgeContent={defaultImages.length}>
        <FormattedMessage id="SectionImages.allImages" />
      </StyledBadge>
    </Button>
  ) : null;

  const viewSurroundPhotosButton =
    surroundImages.length > 0 ? (
      <Button
        onClick={(e) => handleViewPhotosClick('surround', e)}
        size="small"
        startIcon={<SyncIcon />}
        sx={isTablet ? surroundButtonMobileStyle : surroundButtonDesktopStyle}
        variant="contained"
      >
        <StyledBadge badgeContent={surroundImages.length}>
          <FormattedMessage id="ListingPage.viewSurroundImagesButton" />
        </StyledBadge>
      </Button>
    ) : null;

  const imageGridSection = (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      {renderImageGrid(defaultImages.length, processedImages, title, (e) =>
        handleViewPhotosClick('image', e)
      )}
      {viewPhotosButton}
      {viewSurroundPhotosButton}
    </Box>
  );

  // ESC guard: while the carousel is stacked on top, the gallery Modal's own
  // ESC handler must not pop a history entry too (both modals listen on body
  // keyup). Modal has already lifted its scroll lock before calling onClose,
  // so the no-op branch re-disables scrolling for the still-open gallery.
  const handleGalleryClose = () => {
    if (photoIndex !== null) {
      onManageDisableScrolling('ListingPage.gallery', true);
      return;
    }
    goBack();
  };

  const galleryModal = (
    <Modal
      changeCloseButton={
        <IconButton
          aria-label={intl.formatMessage({ id: 'ListingGallery.back' })}
          onClick={goBack}
          sx={galleryBackButtonSx}
        >
          <ChevronLeftIcon sx={{ fontSize: '24px' }} />
        </IconButton>
      }
      containerSx={galleryModalContainerSx}
      id="ListingPage.gallery"
      isOpen={showGallery}
      onClose={handleGalleryClose}
      onManageDisableScrolling={onManageDisableScrolling}
      scrollLayerClassName={LISTING_PAGE_GALLERY_MODAL_SCROLL_LAYER}
      usePortal
    >
      {showGallery ? (
        <ListingGallery images={defaultImages} onPhotoClick={openPhoto} title={title} />
      ) : null}
    </Modal>
  );

  const photoCarouselModal = (
    <Modal
      containerSx={carouselModalContainerSx}
      id="ListingPage.imageCarousel"
      isOpen={photoIndex !== null}
      lightCloseButton
      onClose={goBack}
      onManageDisableScrolling={onManageDisableScrolling}
      scrollLayerClassName={LISTING_PAGE_CAROUSEL_MODAL_SCROLL_LAYER}
      usePortal
    >
      {photoIndex !== null ? (
        <ImageCarousel
          images={defaultImages}
          initialIndex={photoIndex}
          isOpen
          key={`photo-${photoIndex}`}
        />
      ) : null}
    </Modal>
  );

  const openSurroundImagesModal = (
    <Modal
      containerSx={carouselModalContainerSx}
      id="ListingPage.surroundImageCarousel"
      isOpen={surroundImageCarouselOpen}
      lightCloseButton
      onClose={() => setSurroundImageCarouselOpen(false)}
      onManageDisableScrolling={onManageDisableScrolling}
      scrollLayerClassName={LISTING_PAGE_CAROUSEL_MODAL_SCROLL_LAYER}
      usePortal
    >
      <ImageCarousel images={surroundImages} isOpen={surroundImageCarouselOpen} />
    </Modal>
  );

  return (
    <>
      <ListingPageGlobalStyles />
      {isTablet || isMobile ? (
        <>
          <Box sx={{ position: 'relative', mt: isOwnListing ? 10 : 0 }}>
            <IconButton aria-label="go back button" onClick={handleBackButton} sx={backButtonStyle}>
              <ChevronLeftIcon sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>
          <SwiperMobile
            hasImages={hasImages ? defaultImages.length > 0 : null} // Ensure it's true only if there are defaultImages
            images={processedImages}
            isMobile={isTablet || isMobile}
            onImageClick={handleViewPhotosClick}
            title={title}
            viewPhotosButton={viewPhotosButton}
            viewSurroundPhotosButton={viewSurroundPhotosButton}
          />
        </>
      ) : (
        <Box mt={isOwnListing ? 10 : 4}>
          <Container maxWidth="xl" sx={{ height: aspectRatioPadding }}>
            {imageGridSection}
          </Container>
        </Box>
      )}
      {openSurroundImagesModal}
      {galleryModal}
      {photoCarouselModal}
    </>
  );
};

export default memo(SectionImages);
